import { SupabaseClient } from "@supabase/supabase-js"
import JSZip from "jszip"
import { downloadFile } from "./downloadFile"
import { BlobRow } from "../../types/supabase"
import { convertImageBlobToPdf } from "./convertImageBlobToPdf"

/**
 * Downloads multiple blobs as a single ZIP file
 * @param blobs Array of blob records to download
 * @param zipFileName Name for the ZIP file
 * @param options Options including the Supabase client and whether to convert
 *   image bills to PDF (CHM rejects raw image uploads)
 */
export async function downloadBlobsAsZip(
  blobs: Pick<BlobRow, 'file_full_path' | 'file_name' | 'key' | 'content_type'>[],
  zipFileName: string,
  { client, convertImagesToPdf = false }: { client: SupabaseClient; convertImagesToPdf?: boolean }
) {
  if (blobs.length === 0) {
    throw new Error('No blobs to download')
  }

  const zip = new JSZip()

  // Download all blobs and add them to the ZIP
  for (const blob of blobs) {
    const file = await client.storage.from('wcc-hra-calculator-private').download(blob.file_full_path)
    if (file.error) {
      throw new Error(`Failed to download ${blob.file_name}: ${file.error.message}`, { cause: file.error })
    }

    const { data, fileName: convertedFileName } = convertImagesToPdf
      ? await convertImageBlobToPdf(file.data, blob)
      : { data: file.data, fileName: blob.file_name }

    // Use the file name, but ensure uniqueness by appending index if needed.
    // Parse once per blob rather than on every while-loop iteration below.
    const parts = convertedFileName.split('.')
    const ext = parts.length > 1 ? parts.pop() : ''
    const base = parts.join('.')
    let fileName = convertedFileName
    let counter = 1
    while (zip.file(fileName)) {
      fileName = ext ? `${base}_${counter}.${ext}` : `${base}_${counter}`
      counter++
    }

    zip.file(fileName, data)
  }

  // Generate the ZIP file
  const zipBlob = await zip.generateAsync({ type: 'blob' })

  // Ensure the filename ends with .zip
  const finalZipFileName = zipFileName.endsWith('.zip') ? zipFileName : `${zipFileName}.zip`

  // Download the ZIP file
  downloadFile(finalZipFileName, zipBlob)
}
